import { useCallback, useState } from 'react';
import { vars } from '@croquiscom-pvt/zds/tokens';
import { css } from '@emotion/react';

import { Autoplay, Pagination, Swiper, SwiperClass, SwiperSlide } from '@common/carousel/swiper';
import { RollingBannerItem, useIntersect } from '@domains/pdp-common';

export interface RollingBannerProps {
  catalog_product_id: string;
  banner_list: RollingBannerItem[];
  onClick: (url: string, banner_id: string, index: number) => () => void;
  impressBannerUBL: (param: { productId: string; objectId: string; objectIdx: number }) => void;
}

export const RollingBanner: React.FC<React.PropsWithChildren<RollingBannerProps>> = ({
  banner_list,
  catalog_product_id,
  onClick,
  impressBannerUBL,
}) => {
  const [active_index, setActiveIndex] = useState(0);

  const hasMultipleBanners = banner_list.length > 1;

  const intersectionObserverCallback = useCallback(
    (entry) => {
      if (entry.isIntersecting) {
        impressBannerUBL({
          productId: catalog_product_id,
          objectId: banner_list[active_index]?.id,
          objectIdx: active_index,
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [active_index],
  );

  const { targetRef } = useIntersect(intersectionObserverCallback, { threshold: 0.5 });

  const handleSlideChange = (swiper: SwiperClass) => {
    setActiveIndex(swiper.activeIndex);
  };

  return (
    <div css={wrapperCss}>
      <div css={contentCss} ref={targetRef}>
        <Swiper
          loop={hasMultipleBanners}
          modules={[Autoplay, Pagination]}
          pagination={
            hasMultipleBanners
              ? { type: 'fraction', modifierClass: 'rolling__banner--', lockClass: 'rolling__banner--lock' }
              : false
          }
          autoplay={hasMultipleBanners ? { delay: 4000, pauseOnMouseEnter: true, disableOnInteraction: false } : false}
          onSlideChange={handleSlideChange}
        >
          {banner_list.map((item, index) => {
            if (!item.landing_url) {
              return null;
            }

            return (
              <SwiperSlide key={`${item.id}-${index}`}>
                <div className='rolling__banner__item' onClick={onClick(item.landing_url, item.id, index)}>
                  <img css={imgCss} src={item.image_url} alt='' />
                </div>
              </SwiperSlide>
            );
          })}
        </Swiper>
      </div>
    </div>
  );
};

const wrapperCss = css`
  border-top: 8px solid ${vars.v3Color.gray100};
`;

const contentCss = css`
  position: relative;
  height: 96px;

  .rolling__banner__item {
    display: block;
    width: 100%;
    height: 96px;
    text-align: center;
    overflow: hidden;
  }

  .rolling__banner--fraction.swiper-pagination-horizontal {
    position: absolute;
    right: 14px;
    bottom: 16px;
    display: flex;
    align-items: center;
    justify-content: space-around;
    padding: 2px 10px;
    z-index: 2;
    min-width: 40px;
    height: 18px;
    background: rgba(0, 0, 0, 0.5);
    border-radius: 20px;
    color: ${vars.v3Color.staticWhite};

    // Typography.CAPTION_12_MEDIUM
    font-family: ${vars.fontFamily.sans};
    font-size: ${vars.fontSize[12]};
    font-weight: ${vars.fontWeight.medium};
    line-height: ${vars.lineHeight['14px']};

    .swiper-pagination-current {
    }
    .swiper-pagination-total {
      opacity: 0.7;
    }
  }
  .rolling__banner--fraction.swiper-pagination-horizontal.rolling__banner--lock {
    display: none;
  }
`;

const imgCss = css`
  min-width: 360px;
  max-width: 720px;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;
